import DropDown from '@gameday/react-components/components/FormElement/DropDown'
import InputField from '@gameday/react-components/components/InputField'
import ListItem from '@gameday/react-components/components/ListItem'
import LoadingIndicator from '@gameday/react-components/components/LoadingIndicator'
import Pagination from '@gameday/react-components/components/Pagination'
import { isNil } from 'lodash'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import commonStyles from '../../../../styles/common-styles.module.scss'
import MessagesAccordian from '../../Common/MessagesAccordian'
import NavigationButtons from '../../Common/NavigationButton'
import {
  filterLinkedMembers,
  GENDER_FILTER_THRESHOLD,
  genderIdentityOptionsFor,
  genderOptionsFor,
  LIST_PAGE_SIZE,
  SEARCH_THRESHOLD,
} from './filterLinkedMembers'
import styles from './styles.scss'

const StyledListItem = styled.div`
  .MuiListItem-root {
    .MuiRadio-colorPrimary.Mui-checked {
      color: ${props => props.buttonColour};
    }
  }
`

const StyledParticipants = styled.div`
  .rich-text-link {
    color: ${props => props.formSettings.textLinkColour} !important;
  }
`

const toDropDownOptions = options =>
  options.map(option => ({
    ...option,
    name: option.disabled ? `${option.name} (no matching members)` : option.name,
  }))

const displayName = member => [member.firstname || '', member.lastname || ''].join(' ')

function displayListItem(registrations, member) {
  const memberIdentifier = member.id || member.sfid
  // biome-ignore lint/complexity/useOptionalChain: TODO
  const informationCompleted = registrations[memberIdentifier] && registrations[memberIdentifier].isComplete
  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <div style={{ flex: 1 }}>
        <span>{displayName(member)}</span>
        {informationCompleted && <b className={styles.addedTextLabel}> ADDED</b>}
      </div>
      {informationCompleted && (
        <div>
          <b>EDIT</b>
        </div>
      )}
    </div>
  )
}

const ParticipantsComponent = ({
  members,
  registrations,
  onSelected,
  buttonColour,
  buttonTextColour,
  textLinkColour,
  next,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  previous,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  onBack = () => {},
  organisation,
  memberOnly,
  welcomeMessages,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  isTeamEvent,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  teamFormType,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  preventDefaultOnBack,
  formSettings,
  formType,
  selfMember,
}) => {
  const [checked, setChecked] = useState(null)
  const [member, setMember] = useState({})
  const [searchText, setSearchText] = useState('')
  const [genderFilter, setGenderFilter] = useState('')
  const [genderIdentityFilter, setGenderIdentityFilter] = useState('')
  const [page, setPage] = useState(1)

  const memberCount = (members || []).length
  const showSearchControls = memberCount >= SEARCH_THRESHOLD
  const showGenderFilters = memberCount >= GENDER_FILTER_THRESHOLD
  const paginateList = memberCount >= GENDER_FILTER_THRESHOLD
  const genderOptions = useMemo(() => toDropDownOptions(genderOptionsFor(members)), [members])
  const genderIdentityOptions = useMemo(() => toDropDownOptions(genderIdentityOptionsFor(members)), [members])
  const filteredMembers = useMemo(
    () =>
      showSearchControls ? filterLinkedMembers(members, searchText, genderFilter, genderIdentityFilter) : members || [],
    [members, searchText, genderFilter, genderIdentityFilter, showSearchControls]
  )
  const numPages = Math.max(1, Math.ceil(filteredMembers.length / LIST_PAGE_SIZE))
  const activePage = Math.min(page, numPages)
  const visibleMembers = paginateList
    ? filteredMembers.slice((activePage - 1) * LIST_PAGE_SIZE, activePage * LIST_PAGE_SIZE)
    : filteredMembers

  return (
    // biome-ignore lint/complexity/noUselessFragments: TODO
    <>
      <StyledParticipants formSettings={formSettings}>
        {welcomeMessages && <MessagesAccordian messages={welcomeMessages} title="Messages from:" />}

        {members === null ? (
          <div className={commonStyles.loadingContainer}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <h2>{`${
              formType?.toLowerCase() === 'shop' ? 'Who are you purchasing for?' : 'Who would you like to register?'
            }`}</h2>
            {members.length > 0 && (
              <>
                <h3>Linked Member Profile(s)</h3>
                {memberOnly ? <p>This event is restricted to members only.</p> : null}
                {showSearchControls && (
                  <div className={styles.searchControls}>
                    <div className={styles.searchField}>
                      <InputField
                        withBorder
                        fullWidth
                        placeholder="Search by name"
                        value={searchText}
                        onChange={event => {
                          setSearchText(event.target.value)
                          setPage(1)
                        }}
                        isClearable
                        handleOnClear={() => {
                          setSearchText('')
                          setPage(1)
                        }}
                        id="linked-member-search"
                        dataTestId="linkedMemberSearch"
                      />
                    </div>
                    {showGenderFilters && (genderOptions.length > 0 || genderIdentityOptions.length > 0) && (
                      <div className={styles.filterRow}>
                        {genderOptions.length > 0 && (
                          <div className={styles.genderFilter}>
                            <DropDown
                              border
                              fullWidth
                              defaultValue="All Genders At Birth"
                              selected={genderFilter}
                              filterData={[{ key: 'gender', value: genderOptions, key_prefix: 'gender' }]}
                              onChange={value => {
                                setGenderFilter(value)
                                setPage(1)
                              }}
                              id="linked-member-gender-filter"
                              data-testid="linkedMemberGenderFilter"
                            />
                          </div>
                        )}
                        {genderIdentityOptions.length > 0 && (
                          <div className={styles.genderFilter}>
                            <DropDown
                              border
                              fullWidth
                              defaultValue="All Gender Identities"
                              selected={genderIdentityFilter}
                              filterData={[
                                { key: 'genderIdentity', value: genderIdentityOptions, key_prefix: 'genderIdentity' },
                              ]}
                              onChange={value => {
                                setGenderIdentityFilter(value)
                                setPage(1)
                              }}
                              id="linked-member-gender-identity-filter"
                              data-testid="linkedMemberGenderIdentityFilter"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.existingMembersContainer} data-testid="linkedParticipantList">
                  <StyledListItem buttonColour={textLinkColour}>
                    {visibleMembers.map(member => {
                      const memberIdentifier = member.id || member.sfid
                      return (
                        <ListItem
                          className={commonStyles.listItem}
                          isChecked={checked === memberIdentifier}
                          disabled={!!memberOnly && member.organisation !== organisation.sfid}
                          key={memberIdentifier}
                          type="radio"
                          onSelected={() => {
                            setChecked(memberIdentifier)
                            setMember(member)
                          }}
                          id={`participant-${isNil(memberIdentifier) ? 'new' : memberIdentifier}`}
                          data-testid="participant"
                        >
                          {displayListItem(registrations, member)}
                        </ListItem>
                      )
                    })}
                  </StyledListItem>
                  {showSearchControls && filteredMembers.length === 0 && (
                    <p className={styles.noMatches} data-testid="noLinkedMemberMatches">
                      No linked members match your search
                    </p>
                  )}
                  {paginateList && filteredMembers.length > LIST_PAGE_SIZE && (
                    <div className={styles.paginationRow} data-testid="linkedMemberPagination">
                      <Pagination
                        numPage={numPages}
                        activePage={activePage}
                        setPage={setPage}
                        smallPagination
                        hideSizePerPage
                        hideShowingRows
                      />
                    </div>
                  )}
                </div>
              </>
            )}
            {!memberOnly ? (
              <div>
                <h3>New Member Profile</h3>
                <StyledListItem className={styles.newMembersContainer} buttonColour={textLinkColour}>
                  {selfMember &&
                    !(members || []).some(
                      m =>
                        m.email?.toLowerCase() === selfMember.email?.toLowerCase() &&
                        m.firstname?.toLowerCase() === selfMember.firstname?.toLowerCase() &&
                        m.lastname?.toLowerCase() === selfMember.lastname?.toLowerCase() &&
                        m.birthdate === selfMember.birthdate
                    ) &&
                    !(selfMember.sfid && registrations[selfMember.sfid]) && (
                      <ListItem
                        className={commonStyles.listItem}
                        isChecked={checked === 'self'}
                        type="radio"
                        onSelected={() => {
                          setChecked('self')
                          setMember(selfMember)
                        }}
                        id="self-participant"
                        data-testid="selfParticipant"
                      >
                        Registering Myself
                      </ListItem>
                    )}
                  <ListItem
                    className={commonStyles.listItem}
                    isChecked={checked === 'new'}
                    type="radio"
                    onSelected={() => {
                      setChecked('new')
                      setMember(null)
                    }}
                    id="new-participant"
                    data-testid="newParticipant"
                  >
                    New Participant
                  </ListItem>
                  {Object.keys(registrations)
                    .filter(regId => registrations[regId].isNewMember)
                    .map((regId, i) => (
                      <ListItem
                        key={regId}
                        className={commonStyles.listItem}
                        isChecked={checked === regId}
                        type="radio"
                        onSelected={() => {
                          setChecked(regId)
                          setMember(registrations[regId])
                        }}
                        id={`new-participant-${i}`}
                      >
                        {displayListItem(registrations, registrations[regId])}
                      </ListItem>
                    ))}
                </StyledListItem>
              </div>
            ) : null}
            <NavigationButtons
              buttonColour={buttonColour}
              buttonTextColour={buttonTextColour}
              isContinueDisabled={!checked}
              onSubmit={() => {
                onSelected(member)
                next()
              }}
              hideBackButton
            />
          </>
        )}
      </StyledParticipants>
    </>
  )
}

export default ParticipantsComponent
